import { z } from "zod";
import { PlayerRef } from "../common.ts";
import {
  AdministrativeAreasQueryV1,
  AdministrativeAreasV1,
  type OperatorPermission,
  type PlatformPermission,
} from "../ports.ts";
import { AuditQueryV1, AuditResponseV1 } from "./audit.ts";
import {
  BlockListQueryV1,
  BlockListResponseV1,
  CreateBlockRequestV1,
  UnblockRequestV1,
} from "./blocks.ts";
import { BlockEntryV1 } from "./common.ts";
import type { ExplorerApiErrorCode } from "./errors.ts";
import {
  DeviceListQueryV1,
  DeviceListResponseV1,
  KpisQueryV1,
  KpisResponseV1,
  UserListQueryV1,
  UserListResponseV1,
} from "./lists.ts";
import {
  AreaEntitiesRequestV1,
  AreaEntitiesResponseV1,
  MapClustersQueryV1,
  MapClustersResponseV1,
} from "./map.ts";
import {
  NotificationsQueryV1,
  NotificationsResponseV1,
  PreferencesResponseV1,
  UpdatePreferencesRequestV1,
} from "./preferences.ts";
import {
  DevicePlayersQueryV1,
  DevicePlayersResponseV1,
  DeviceProfileResponseV1,
  ProfileQueryV1,
  SubjectPageQueryV1,
  SubjectPeriodQueryV1,
  TimelineQueryV1,
  TimelineResponseV1,
  UserDevicesResponseV1,
  UserProfileResponseV1,
} from "./profiles.ts";
import {
  CreateExportRequestV1,
  DownloadExportQueryV1,
  ExportDownloadV1,
  ExportsQueryV1,
  ExportsResponseV1,
  ExportV1,
} from "./exports.ts";
import { CreateNoteRequestV1, NotesQueryV1, NotesResponseV1, NoteV1 } from "./notes.ts";
import { SearchQueryV1, SearchResponseV1 } from "./search.ts";
import {
  IpTransactionsQueryV1,
  IpTransactionsResponseV1,
  SubjectTransactionsQueryV1,
  TransactionDetailQueryV1,
  TransactionDetailResponseV1,
  TransactionListResponseV1,
  TransactionLocationsQueryV1,
  TransactionLocationsResponseV1,
} from "./transactions.ts";

/** Base path of the Explorer API (served by the `explorer` role, same origin as the SPA). */
export const EXPLORER_API_BASE = "/explorer/v1";

/** Path parameters of the Explorer routes. */
export const PlayerRefParams = z.object({ ref: PlayerRef }).strict();
export const DeviceRefParams = z.object({ ref: z.uuid() }).strict();
export const IdParams = z.object({ id: z.uuid() }).strict();
export const IpParams = z.object({ ip: z.union([z.ipv4(), z.ipv6()]) }).strict();

/** Who may call an Explorer API route (`x-strust-permission` of the OpenAPI document). */
export type ExplorerApiPermission =
  | OperatorPermission
  | PlatformPermission
  /** Any live console session, no permission code. */
  | "authenticated"
  /** No session (login, SSO, invitation links): rate limited per client IP. */
  | "public";

/**
 * One route of the Explorer API (`/explorer/v1`, design D4), whichever module serves it: method,
 * path (OpenAPI template), who may call it, its schemas and the error codes it answers. The route
 * tables of this package drive the OpenAPI document (`openapi/explorer-v1.json`).
 */
export interface ExplorerApiRoute {
  readonly operationId: string;
  readonly method: "GET" | "POST" | "PUT" | "PATCH";
  /** Relative to {@link EXPLORER_API_BASE}, with `{param}` placeholders. */
  readonly path: string;
  readonly permission: ExplorerApiPermission;
  /** Also needs `location.precise.read` to include precise coordinates (never required). */
  readonly preciseLocation?: boolean;
  readonly summary: string;
  /** Longer explanation (redirect targets, idempotency, how a missing permission answers). */
  readonly description?: string;
  readonly params?: z.ZodObject;
  readonly query?: z.ZodType;
  readonly body?: z.ZodType;
  /**
   * JSON body of {@link successStatus} (for `302`, the body sent along with `Location`); absent
   * for `204` and `303` (no body).
   */
  readonly response?: z.ZodType;
  readonly successStatus: 200 | 201 | 202 | 204 | 302 | 303;
  /** Description of the success answer when the default one of its status does not fit. */
  readonly successDescription?: string;
  /** A second success status with the same body (`PUT /organization/identity-provider`). */
  readonly alternativeSuccess?: { readonly status: 200 | 201; readonly description: string };
  /**
   * A `202` answer with its own body: the password was right but the login needs a further step
   * (TOTP code or mandatory two-factor set-up) before a session starts; it sets the login-step
   * cookie instead of the session cookies (change add-console-account-settings, design D5).
   */
  readonly loginStep?: { readonly response: z.ZodType; readonly description: string };
  /** `Location` of a `303` answer. */
  readonly redirect?: string;
  /** Cookies the success answer sets (`Set-Cookie`). */
  readonly setsCookies?: "console_session" | "console_session_cleared" | "docs_access";
  /** The route needs the login-step cookie of a `202` login answer (change add-console-account-settings). */
  readonly needsLoginStep?: true;
  /**
   * The route may instead answer `200` with a `text/csv` attachment (`POST /exports` up to 10,000
   * rows); {@link successStatus} is then the background answer.
   */
  readonly csv?: boolean;
  /**
   * Reference data that changes only with a dataset version: answered with
   * `Cache-Control: private, max-age=86400` and an `ETag` (a matching `If-None-Match` answers
   * `304` without a body) instead of `no-store`.
   */
  readonly cacheable?: true;
  /**
   * Public, configuration-only data (`GET /installation`): answered with
   * `Cache-Control: public, max-age=<seconds>` instead of `no-store`.
   */
  readonly publicMaxAgeSeconds?: number;
  readonly errors: readonly ExplorerApiErrorCode[];
}

/**
 * A route of the `explorer` module (design D4): the `AccessGuard` checks an operator permission
 * code (`authenticated` = any live console session). The module's Fastify plugin and the OpenAPI
 * generator both read this table.
 */
export interface ExplorerRoute extends ExplorerApiRoute {
  readonly method: "GET" | "POST" | "PUT";
  readonly permission: OperatorPermission | "authenticated";
  readonly response: z.ZodType;
  readonly successStatus: 200 | 201 | 202 | 302;
}

const READ_ERRORS = [
  "unauthenticated",
  "forbidden",
  "invalid_request",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
const RESOURCE_ERRORS = [...READ_ERRORS, "not_found"] as const;
const WRITE_ERRORS = [...RESOURCE_ERRORS, "csrf_token_invalid"] as const;

export const EXPLORER_ROUTES = [
  {
    operationId: "search",
    method: "GET",
    path: "/search",
    permission: "explorer.read",
    summary: "Search by player_ref, device_ref, verification_id or IP address.",
    query: SearchQueryV1,
    response: SearchResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "listUsers",
    method: "GET",
    path: "/users",
    permission: "explorer.read",
    summary: "Users of the period with filters, sorting and 20 rows per page.",
    query: UserListQueryV1,
    response: UserListResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "listDevices",
    method: "GET",
    path: "/devices",
    permission: "explorer.read",
    summary: "Devices of the period with filters, sorting and 20 rows per page.",
    query: DeviceListQueryV1,
    response: DeviceListResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "getPeriodKpis",
    method: "GET",
    path: "/kpis",
    permission: "explorer.read",
    summary: "Period indicators: new users, transactions and failure rate.",
    query: KpisQueryV1,
    response: KpisResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "listIpTransactions",
    method: "GET",
    path: "/ips/{ip}/transactions",
    permission: "explorer.read",
    preciseLocation: true,
    summary:
      "Transactions from an IP in the period (last 180 days) with distinct players and devices.",
    params: IpParams,
    query: IpTransactionsQueryV1,
    response: IpTransactionsResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "getUserProfile",
    method: "GET",
    path: "/users/{ref}",
    permission: "explorer.read",
    summary: "User profile header and indicators (audited read).",
    params: PlayerRefParams,
    query: ProfileQueryV1,
    response: UserProfileResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "getUserTimeline",
    method: "GET",
    path: "/users/{ref}/timeline",
    permission: "explorer.read",
    summary: "Approved and denied transactions per day, optionally for one failure reason.",
    params: PlayerRefParams,
    query: TimelineQueryV1,
    response: TimelineResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "listUserTransactions",
    method: "GET",
    path: "/users/{ref}/transactions",
    permission: "explorer.read",
    preciseLocation: true,
    summary: "Transactions of a player for the map, side list and table (newest first).",
    params: PlayerRefParams,
    query: SubjectTransactionsQueryV1,
    response: TransactionListResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "listUserDevices",
    method: "GET",
    path: "/users/{ref}/devices",
    permission: "explorer.read",
    summary: "Devices tab of a user profile.",
    params: PlayerRefParams,
    query: SubjectPageQueryV1,
    response: UserDevicesResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "getTransactionLocations",
    method: "GET",
    path: "/transactions/locations",
    permission: "location.precise.read",
    summary: "Precise coordinates of transactions on demand (audited precise_location_viewed).",
    query: TransactionLocationsQueryV1,
    response: TransactionLocationsResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "getTransactionDetail",
    method: "GET",
    path: "/transactions/{id}",
    permission: "explorer.read",
    preciseLocation: true,
    summary: "Transaction detail with the signature check of its signed result (audited read).",
    params: IdParams,
    query: TransactionDetailQueryV1,
    response: TransactionDetailResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "getDeviceProfile",
    method: "GET",
    path: "/devices/{ref}",
    permission: "explorer.read",
    summary: "Device profile header and indicators (audited read).",
    params: DeviceRefParams,
    query: SubjectPeriodQueryV1,
    response: DeviceProfileResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "listDevicePlayers",
    method: "GET",
    path: "/devices/{ref}/players",
    permission: "explorer.read",
    summary: "Players that used the device in the period.",
    params: DeviceRefParams,
    query: DevicePlayersQueryV1,
    response: DevicePlayersResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "listDeviceTransactions",
    method: "GET",
    path: "/devices/{ref}/transactions",
    permission: "explorer.read",
    preciseLocation: true,
    summary: "Transactions of a device for the map, side list and table (newest first).",
    params: DeviceRefParams,
    query: SubjectTransactionsQueryV1,
    response: TransactionListResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "getMapClusters",
    method: "GET",
    path: "/map/clusters",
    permission: "explorer.read",
    preciseLocation: true,
    summary: "Overview map clusters (or precise markers at zoom 12+ with location.precise.read).",
    query: MapClustersQueryV1,
    response: MapClustersResponseV1,
    successStatus: 200,
    errors: [...READ_ERRORS, "rate_limited"],
  },
  {
    operationId: "listAreaEntities",
    method: "POST",
    path: "/map/areas/entities",
    permission: "explorer.read",
    summary: "Users and transactions inside a cluster cell or a drawn rectangle/polygon.",
    body: AreaEntitiesRequestV1,
    response: AreaEntitiesResponseV1,
    successStatus: 200,
    errors: [...READ_ERRORS, "csrf_token_invalid", "rate_limited"],
  },
  {
    operationId: "listBlocks",
    method: "GET",
    path: "/blocks",
    permission: "explorer.read",
    summary: "Blocklist (Users and Devices tabs) with search, reason and period filters.",
    query: BlockListQueryV1,
    response: BlockListResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "blockSubject",
    method: "POST",
    path: "/blocks",
    permission: "blocklist.write",
    summary: "Block a player_ref or device_ref in the installation's environment.",
    body: CreateBlockRequestV1,
    response: BlockEntryV1,
    successStatus: 201,
    errors: [...WRITE_ERRORS, "already_blocked"],
  },
  {
    operationId: "unblockSubject",
    method: "POST",
    path: "/blocks/{id}/unblock",
    permission: "blocklist.write",
    summary: "Lift an active block with a reason.",
    params: IdParams,
    body: UnblockRequestV1,
    response: BlockEntryV1,
    successStatus: 200,
    errors: [...WRITE_ERRORS, "block_not_active"],
  },
  {
    operationId: "listNotes",
    method: "GET",
    path: "/users/{ref}/notes",
    permission: "explorer.read",
    summary: "Internal notes of a user profile, newest first (author and date; never edited).",
    params: PlayerRefParams,
    query: NotesQueryV1,
    response: NotesResponseV1,
    successStatus: 200,
    errors: RESOURCE_ERRORS,
  },
  {
    operationId: "addNote",
    method: "POST",
    path: "/users/{ref}/notes",
    permission: "notes.write",
    summary: "Add an internal note (1-2,000 characters) to a user profile (audited, immutable).",
    params: PlayerRefParams,
    body: CreateNoteRequestV1,
    response: NoteV1,
    successStatus: 201,
    errors: WRITE_ERRORS,
  },
  {
    operationId: "createExport",
    method: "POST",
    path: "/exports",
    permission: "explorer.export",
    preciseLocation: true,
    summary:
      "Export a filtered list as CSV: up to 10,000 rows answered at once (200 text/csv), otherwise 202 and generated in the background (audited).",
    body: CreateExportRequestV1,
    response: ExportV1,
    successStatus: 202,
    csv: true,
    errors: [...WRITE_ERRORS, "export_limit_reached", "rate_limited"],
  },
  {
    operationId: "listExports",
    method: "GET",
    path: "/exports",
    permission: "explorer.export",
    summary: "Exports requested by the session's console user, newest first.",
    query: ExportsQueryV1,
    response: ExportsResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "downloadExport",
    method: "GET",
    path: "/exports/{id}/download",
    permission: "explorer.export",
    preciseLocation: true,
    summary:
      "Download a ready export: re-checks owner, operator and permission, audits the download and redirects (302) to a 5-minute presigned URL.",
    params: IdParams,
    query: DownloadExportQueryV1,
    response: ExportDownloadV1,
    successStatus: 302,
    errors: [...RESOURCE_ERRORS, "export_not_ready", "export_expired"],
  },
  {
    operationId: "queryAudit",
    method: "GET",
    path: "/audit",
    permission: "audit.read",
    summary: "Explorer and iam audit logs merged, newest first (no edit or delete route).",
    query: AuditQueryV1,
    response: AuditResponseV1,
    successStatus: 200,
    errors: READ_ERRORS,
  },
  {
    operationId: "getPreferences",
    method: "GET",
    path: "/me/preferences",
    permission: "authenticated",
    summary: "Column preferences of the session's console user.",
    response: PreferencesResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "server_error"],
  },
  {
    operationId: "updatePreferences",
    method: "PUT",
    path: "/me/preferences",
    permission: "authenticated",
    summary: "Save the visible columns of one screen.",
    body: UpdatePreferencesRequestV1,
    response: PreferencesResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "csrf_token_invalid", "invalid_request", "server_error"],
  },
  {
    operationId: "listNotifications",
    method: "GET",
    path: "/me/notifications",
    permission: "authenticated",
    summary: "In-app notifications of the session's console user, newest first.",
    query: NotificationsQueryV1,
    response: NotificationsResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "invalid_request", "server_error"],
  },
  {
    operationId: "listAdministrativeAreas",
    method: "GET",
    path: "/reference/administrative-areas",
    permission: "authenticated",
    summary:
      "Countries, or one country's states, of the GeoBoundaryDataset that the checks resolve to (for the country and state pickers).",
    description:
      "Without `country`: every ISO 3166-1 alpha-2 country of the dataset; with `country`: its ISO 3166-2 states (an unknown country has none). Names are the dataset's (English); the console localizes country names. Cacheable: the answer changes only with `boundary_dataset_version`.",
    query: AdministrativeAreasQueryV1,
    response: AdministrativeAreasV1,
    successStatus: 200,
    cacheable: true,
    errors: ["unauthenticated", "invalid_request", "server_error"],
  },
] as const satisfies readonly ExplorerRoute[];

export type ExplorerOperationId = (typeof EXPLORER_ROUTES)[number]["operationId"];

/** The route of an operation id (typed lookup for the Fastify plugin). */
export function explorerRoute(operationId: ExplorerOperationId): ExplorerRoute {
  const route = EXPLORER_ROUTES.find((r) => r.operationId === operationId);
  if (route === undefined) throw new Error(`Unknown Explorer operation ${operationId}`);
  return route;
}
